import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, CaretDown, CaretUp, Scissors, Drop, Plant, SprayBottle, Sun, Snowflake, Leaf, Fire, Check, NotePencil } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const CATEGORY_CONFIG = {
  mowing: { icon: Scissors, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Mowing' },
  fertilizing: { icon: Plant, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30', label: 'Fertilizing' },
  herbicide: { icon: SprayBottle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Weed Control' },
  watering: { icon: Drop, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Watering' },
  general: { icon: BookOpen, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'General' },
};

const SEASON_ICONS = {
  spring: { icon: Leaf, color: 'text-green-500' },
  summer: { icon: Fire, color: 'text-orange-500' },
  fall: { icon: Leaf, color: 'text-amber-500' },
  winter: { icon: Snowflake, color: 'text-blue-400' },
};

const LOGGABLE_CATEGORIES = new Set(['fertilizing', 'herbicide']);

export const LawnCareRules = ({ grassType, onSprayLogged }) => {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [togglingRule, setTogglingRule] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/rules/lawn-care`, { withCredentials: true })
      .then(res => {
        setRules(res.data);
        if (res.data?.categories?.length > 0) {
          setExpandedCategory(res.data.categories[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggleRule = async (ruleId, currentlyCompleted, ruleText, categoryId) => {
    setTogglingRule(ruleId);
    const newCompleted = !currentlyCompleted;
    const canLog = LOGGABLE_CATEGORIES.has(categoryId) && newCompleted;

    try {
      const res = await axios.post(`${API_URL}/api/rules/lawn-care/complete`, {
        rule_id: ruleId,
        completed: newCompleted,
        log_to_spray: canLog,
        rule_text: ruleText,
      }, { withCredentials: true });

      // Update local state
      setRules(prev => {
        const updated = { ...prev };
        updated.categories = prev.categories.map(cat => ({
          ...cat,
          rules: cat.rules.map(r => r.id === ruleId ? { ...r, completed: newCompleted } : r),
        }));
        const completed = updated.categories.reduce((acc, c) => acc + c.rules.filter(r => r.completed).length, 0);
        updated.progress = { ...prev.progress, completed };
        return updated;
      });

      if (newCompleted) {
        if (res.data.spray_log_id) {
          toast.success('Done! Also logged to your spray history.');
          if (onSprayLogged) onSprayLogged();
        } else {
          toast.success('Marked as done');
        }
      } else {
        toast('Unmarked');
      }
    } catch {
      toast.error('Failed to update rule');
    } finally {
      setTogglingRule(null);
    }
  };

  if (loading || !rules) return null;

  const SeasonIcon = SEASON_ICONS[rules.season]?.icon || Sun;
  const seasonColor = SEASON_ICONS[rules.season]?.color || 'text-yellow-500';
  const progressPct = rules.progress.total > 0 ? Math.round((rules.progress.completed / rules.progress.total) * 100) : 0;

  return (
    <Card data-testid="lawn-care-rules-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={20} className="text-emerald-600 dark:text-emerald-400" />
            Lawn Care Rules
          </div>
          <div className="flex items-center gap-1.5">
            <SeasonIcon size={16} className={seasonColor} />
            <span className="text-xs font-medium text-muted-foreground capitalize">{rules.season} — {rules.grass_label}</span>
          </div>
        </CardTitle>
        {/* Progress bar */}
        <div className="flex items-center gap-3 pt-1">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
              data-testid="rules-progress-bar"
            />
          </div>
          <span className="text-xs font-semibold text-muted-foreground" data-testid="rules-progress-text">
            {rules.progress.completed}/{rules.progress.total}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {rules.categories.map((cat) => {
          const config = CATEGORY_CONFIG[cat.id] || CATEGORY_CONFIG.general;
          const Icon = config.icon;
          const isExpanded = expandedCategory === cat.id;
          const catCompleted = cat.rules.filter(r => r.completed).length;

          return (
            <div key={cat.id} className="border rounded-lg overflow-hidden" data-testid={`rules-category-${cat.id}`}>
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
                data-testid={`rules-toggle-${cat.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center`}>
                    <Icon size={16} className={config.color} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{config.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {catCompleted === cat.rules.length ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">All done</span>
                      ) : (
                        `${catCompleted}/${cat.rules.length}`
                      )}
                    </span>
                  </div>
                </div>
                {isExpanded ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-1">
                  {cat.rules.map((rule, i) => {
                    const isToggling = togglingRule === rule.id;
                    const canLog = LOGGABLE_CATEGORIES.has(cat.id);
                    return (
                      <div
                        key={rule.id}
                        className={`flex gap-2 py-2 px-1 rounded-md border-t border-border/50 first:border-t-0 transition-colors ${
                          rule.completed ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''
                        }`}
                        data-testid={`rule-item-${rule.id}`}
                      >
                        <button
                          onClick={() => handleToggleRule(rule.id, rule.completed, rule.text, cat.id)}
                          disabled={isToggling}
                          className={`shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${
                            rule.completed
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-muted-foreground/40 hover:border-emerald-500'
                          } ${isToggling ? 'opacity-50' : 'cursor-pointer'}`}
                          data-testid={`rule-check-${rule.id}`}
                        >
                          {rule.completed && <Check size={12} weight="bold" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-1">
                            <div className={`w-1 shrink-0 rounded-full mt-1 ${
                              rule.severity === 'critical' ? 'bg-red-500' :
                              rule.severity === 'important' ? 'bg-amber-500' :
                              'bg-green-500'
                            }`} style={{ minHeight: '0.75rem' }} />
                            <p className={`text-sm leading-relaxed ${rule.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {rule.text}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-2 mt-0.5">
                            {rule.source && (
                              <a
                                href={rule.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Source: {rule.source}
                              </a>
                            )}
                            {canLog && !rule.completed && (
                              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                <NotePencil size={10} />
                                Logs to spray history
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <p className="text-[10px] text-muted-foreground text-center pt-1">
          Guidelines based on university research. Always follow product labels and local conditions.
        </p>
      </CardContent>
    </Card>
  );
};
